import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Wrench, ArrowRight } from "lucide-react";
import { PageHero, Reveal } from "@/components/Reveal";
import { ShareChampionButton } from "@/components/ShareCard";
import { api, photoUrl } from "@/lib/api";
import { IMG } from "@/lib/content";
import { useLang } from "@/context/LanguageContext";

const monthLabel = (m, lang) => new Date(`${m}-01T00:00:00`).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { month: "long", year: "numeric" });

const ChampionCard = ({ c, i }) => {
  const { L, lang } = useLang();
  const photo = c.project?.photos?.[0];
  const story = L(c.story, c.story_hi || c.story);
  return (
    <Reveal delay={0.05 * (i % 3)}>
      <article className="h-full rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-[0_8px_30px_rgba(10,25,47,0.08)] flex flex-col" data-testid={`wall-card-${c.month}`}>
        <div className="relative aspect-[4/3] bg-white">
          {photo ? <img src={photoUrl(photo)} alt={c.project.title} className="w-full h-full object-cover" />
            : <div className="w-full h-full grid place-items-center"><Trophy className="w-16 h-16 text-[#FF2E63]" /></div>}
          <span className="absolute top-3 left-3 bg-[#FF3B3B] text-white text-xs font-bold px-3 py-1 rounded-full" data-testid={`wall-month-${c.month}`}>{monthLabel(c.month, lang)}</span>
        </div>
        <div className="p-6 flex flex-col flex-1">
          <h3 className="text-2xl font-extrabold" data-testid={`wall-name-${c.month}`}>{c.first_name}</h3>
          <p className="text-sm text-slate-500">{L(`Class ${c.class_grade}`, `कक्षा ${c.class_grade}`)} · {c.school_name} · {c.program}</p>
          {c.project && <p className="mt-3 text-sm text-[#007A8A] font-semibold inline-flex items-center gap-2"><Wrench className="w-4 h-4" />{c.project.title}</p>}
          <p className="mt-3 text-slate-700 text-sm leading-relaxed flex-1" data-testid={`wall-story-${c.month}`}>
            {story || L(`Top builder of the month with ${c.projects} completed projects.`, `${c.projects} पूरे प्रोजेक्ट के साथ महीने के सर्वश्रेष्ठ मेकर।`)}
          </p>
          <div className="mt-5">
            <ShareChampionButton variant="orange" testid={`wall-share-${c.month}`}
              data={{ first_name: c.first_name, school_name: c.school_name, month: c.month, program: c.program, photo }} />
          </div>
        </div>
      </article>
    </Reveal>
  );
};

export default function ChampionWall() {
  const { L } = useLang();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/champions").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  return (
    <div data-testid="champion-wall-page">
      <PageHero chip={L("Champion Wall", "चैंपियन वॉल")} title={L("Every Maker of the Month.", "हर मेकर ऑफ़ द मंथ।")}
        subtitle={L("The students who built the most each month across RoboSpark partner schools.", "हर महीने RoboSpark साझेदार स्कूलों में सबसे ज़्यादा बनाने वाले छात्र।")} image={IMG.robot} testid="wall-hero" />
      <section className="bg-[#F0F4F8] text-[#0A192F] py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {items && items.length === 0 && (
            <div className="text-center py-16 text-slate-500" data-testid="wall-empty">
              <Trophy className="w-12 h-12 mx-auto text-[#FF2E63]" />
              <p className="mt-4">{L("Our first Maker of the Month will be announced soon.", "हमारे पहले मेकर ऑफ़ द मंथ की घोषणा जल्द होगी।")}</p>
              <Link to="/leaderboard" className="mt-4 inline-flex items-center gap-1 text-[#FF2E63] font-semibold">{L("See the leaderboard", "लीडरबोर्ड देखें")} <ArrowRight className="w-4 h-4" /></Link>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="wall-grid">
            {(items || []).map((c, i) => <ChampionCard key={c.month} c={c} i={i} />)}
          </div>
        </div>
      </section>
    </div>
  );
}
